<aside
  class="detail-pane"
  [attr.aria-label]="'DOCUMENT_MANAGEMENT.DETAIL_PANE_ARIA_LABEL' | translate">
  @if (showEmptyState()) {
    <div class="detail-pane__empty">
      <i class="pi pi-file detail-pane__empty-icon" aria-hidden="true"></i>
      <p class="detail-pane__empty-title">
        {{ 'DOCUMENT_MANAGEMENT.DETAIL_EMPTY_TITLE' | translate }}
      </p>
      <p class="detail-pane__empty-hint">
        {{ 'DOCUMENT_MANAGEMENT.DETAIL_EMPTY_HINT' | translate }}
      </p>
    </div>
  } @else if (isLoading()) {
    <div class="detail-pane__loading">
      <p-progressSpinner styleClass="detail-pane__spinner" strokeWidth="3" />
    </div>
  } @else if (hasError()) {
    <div class="detail-pane__error">
      <span>{{ 'DOCUMENT_MANAGEMENT.DETAIL_LOAD_ERROR' | translate }}</span>
    </div>
  } @else if (selection()!.kind === 'folder' && folder()) {
    <header class="detail-pane__header">
      <i class="pi pi-folder detail-pane__folder-icon" aria-hidden="true"></i>
      <div class="detail-pane__header-text">
        @if (titleEditing()) {
          <label class="detail-pane__title-edit-label">
            <span class="detail-pane__visually-hidden">
              {{ 'DOCUMENT_MANAGEMENT.RENAME' | translate }}
            </span>
            <input
              #titleInput
              pInputText
              type="text"
              class="detail-pane__title-input"
              [value]="titleDraft()"
              [disabled]="titleSaving()"
              [attr.placeholder]="'DOCUMENT_MANAGEMENT.FOLDER_NAME_PLACEHOLDER' | translate"
              [attr.aria-invalid]="titleError() ? true : null"
              (input)="onTitleInput($event)"
              (keydown)="onTitleKeydown($event)"
              (blur)="onSaveRename()" />
          </label>
          @if (titleError()) {
            <p class="detail-pane__title-error" role="alert">{{ titleError() }}</p>
          }
        } @else {
          <h2
            class="detail-pane__title"
            [class.detail-pane__title--editable]="canRename()"
            [attr.role]="canRename() ? 'button' : null"
            [attr.tabindex]="canRename() ? 0 : null"
            [attr.aria-label]="canRename() ? ('DOCUMENT_MANAGEMENT.RENAME' | translate) : null"
            (click)="canRename() && onStartRename()"
            (keydown.enter)="canRename() && onStartRename()"
            (keydown.space)="canRename() && onStartRename(); $event.preventDefault()">
            {{ folder()!.displayName ?? folder()!.name }}
            @if (canRename()) {
              <i class="pi pi-pencil detail-pane__title-pencil" aria-hidden="true"></i>
            }
          </h2>
        }
        @if (folderSubtitle() && !titleEditing()) {
          <p class="detail-pane__subtitle">{{ folderSubtitle() }}</p>
        }
      </div>
      <div class="detail-pane__header-actions">
        @if (canStar()) {
          <p-button
            [icon]="isHeaderStarred() ? 'pi pi-star-fill' : 'pi pi-star'"
            [text]="true"
            size="small"
            [styleClass]="
              'detail-pane__star-btn ' + (isHeaderStarred() ? 'detail-pane__star-btn--on' : '')
            "
            [ariaLabel]="
              (isHeaderStarred() ? 'DOCUMENT_MANAGEMENT.UNSTAR' : 'DOCUMENT_MANAGEMENT.STAR')
                | translate
            "
            [pTooltip]="
              (isHeaderStarred() ? 'DOCUMENT_MANAGEMENT.UNSTAR' : 'DOCUMENT_MANAGEMENT.STAR')
                | translate
            "
            (onClick)="onToggleHeaderStar()" />
        }
        @if (canDeleteFolder()) {
          <p-button
            icon="pi pi-trash"
            [text]="true"
            size="small"
            severity="danger"
            [loading]="lifecycleSaving()"
            [ariaLabel]="'DOCUMENT_MANAGEMENT.DELETE' | translate"
            [pTooltip]="'DOCUMENT_MANAGEMENT.DELETE' | translate"
            (onClick)="onDeleteFolder()" />
        }
      </div>
    </header>

    @if (lifecycleError() || starError()) {
      <p class="detail-pane__lifecycle-error" role="alert">{{ lifecycleError() ?? starError() }}</p>
    }

    <section class="detail-pane__section">
      <h3 class="detail-pane__section-title">{{ 'TAGS.SECTION_TITLE' | translate }}</h3>
      <tf-tag-editor
        [tags]="folder()!.tags"
        [availableTags]="availableTags()"
        [editable]="canEditTags()"
        [canCreate]="canCreateTags()"
        (tagsChange)="onTagsChange($event)"
        (tagCreate)="onTagCreate($event)"
        (searchOpened)="onTagSearchOpened()" />
    </section>

    <section class="detail-pane__section">
      <h3 class="detail-pane__section-title">{{ 'DOCUMENT_MANAGEMENT.DETAILS' | translate }}</h3>
      <dl class="detail-pane__kv">
        <div class="detail-pane__kv-row">
          <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_KIND' | translate }}</dt>
          <dd>{{ 'DOCUMENT_MANAGEMENT.FOLDER_KIND.' + (folder()!.kind ?? 'user') | translate }}</dd>
        </div>
        <div class="detail-pane__kv-row">
          <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_CREATED_AT' | translate }}</dt>
          <dd>
            @if (folder()!.createdAt) {
              {{ folder()!.createdAt | date: 'M/d/yyyy, h:mm a' }}
            } @else {
              —
            }
          </dd>
        </div>
        @if (folder()!.createdByName || folder()!.createdBy) {
          <div class="detail-pane__kv-row">
            <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_CREATED_BY' | translate }}</dt>
            @if (folder()!.createdByName) {
              <dd>{{ folder()!.createdByName }}</dd>
            } @else {
              <dd class="detail-pane__id-mono">{{ folder()!.createdBy }}</dd>
            }
          </div>
        }
      </dl>
    </section>

    @if (folderRecentDocuments().length > 0) {
      <section class="detail-pane__section">
        <h3 class="detail-pane__section-title">
          {{ 'DOCUMENT_MANAGEMENT.RECENTLY_ADDED' | translate }}
        </h3>
        <ul class="detail-pane__recent">
          @for (doc of folderRecentDocuments(); track doc.id) {
            <li>
              <button
                type="button"
                class="detail-pane__recent-row"
                [attr.aria-label]="doc.name"
                (click)="onRecentClick(doc)">
                <span
                  class="detail-pane__recent-thumb"
                  [class]="mimeColorClassFor(doc.mimeType)"
                  aria-hidden="true">
                  {{ mimeShortLabel(doc.mimeType) }}
                </span>
                <span class="detail-pane__recent-meta">
                  <span class="detail-pane__recent-name" [title]="doc.name">{{ doc.name }}</span>
                  <span class="detail-pane__recent-sub">
                    @if (doc.createdAt) {
                      {{ doc.createdAt | date: 'M/d/yyyy, h:mm a' }}
                    }
                  </span>
                </span>
              </button>
            </li>
          }
        </ul>
      </section>
    }
  } @else if (selection()!.kind === 'document' && document()) {
    <header class="detail-pane__header">
      <span class="detail-pane__doc-thumb" [class]="currentMimeClass()" aria-hidden="true">
        {{ mimeShortLabel(document()!.mimeType) }}
      </span>
      <div class="detail-pane__header-text">
        @if (titleEditing()) {
          <label class="detail-pane__title-edit-label">
            <span class="detail-pane__visually-hidden">
              {{ 'DOCUMENT_MANAGEMENT.RENAME' | translate }}
            </span>
            <input
              #titleInput
              pInputText
              type="text"
              class="detail-pane__title-input"
              [value]="titleDraft()"
              [disabled]="titleSaving()"
              [attr.placeholder]="'DOCUMENT_MANAGEMENT.FOLDER_NAME_PLACEHOLDER' | translate"
              [attr.aria-invalid]="titleError() ? true : null"
              (input)="onTitleInput($event)"
              (keydown)="onTitleKeydown($event)"
              (blur)="onSaveRename()" />
          </label>
          @if (titleError()) {
            <p class="detail-pane__title-error" role="alert">{{ titleError() }}</p>
          }
        } @else {
          <h2
            class="detail-pane__title"
            [class.detail-pane__title--editable]="canRename()"
            [attr.role]="canRename() ? 'button' : null"
            [attr.tabindex]="canRename() ? 0 : null"
            [attr.aria-label]="canRename() ? ('DOCUMENT_MANAGEMENT.RENAME' | translate) : null"
            (click)="canRename() && onStartRename()"
            (keydown.enter)="canRename() && onStartRename()"
            (keydown.space)="canRename() && onStartRename(); $event.preventDefault()">
            {{ document()!.name }}
            @if (canRename()) {
              <i class="pi pi-pencil detail-pane__title-pencil" aria-hidden="true"></i>
            }
          </h2>
        }
        @if (!titleEditing()) {
          <p class="detail-pane__subtitle">
            {{ formatSize(document()!.size) }}
          </p>
        }
        @if (isSoftDeleted()) {
          <p class="detail-pane__subtitle detail-pane__subtitle--deleted">
            {{ 'DOCUMENT_MANAGEMENT.DELETED_PILL' | translate }}
          </p>
        }
      </div>
      <div class="detail-pane__header-actions">
        @if (canStar()) {
          <p-button
            [icon]="isHeaderStarred() ? 'pi pi-star-fill' : 'pi pi-star'"
            [text]="true"
            size="small"
            [styleClass]="
              'detail-pane__star-btn ' + (isHeaderStarred() ? 'detail-pane__star-btn--on' : '')
            "
            [ariaLabel]="
              (isHeaderStarred() ? 'DOCUMENT_MANAGEMENT.UNSTAR' : 'DOCUMENT_MANAGEMENT.STAR')
                | translate
            "
            [pTooltip]="
              (isHeaderStarred() ? 'DOCUMENT_MANAGEMENT.UNSTAR' : 'DOCUMENT_MANAGEMENT.STAR')
                | translate
            "
            (onClick)="onToggleHeaderStar()" />
        }
        @if (canRestore()) {
          <p-button
            icon="pi pi-replay"
            [text]="true"
            size="small"
            [loading]="lifecycleSaving()"
            [ariaLabel]="'DOCUMENT_MANAGEMENT.RESTORE' | translate"
            [pTooltip]="'DOCUMENT_MANAGEMENT.RESTORE' | translate"
            (onClick)="onRestore()" />
        }
        @if (canHardDelete()) {
          <p-button
            icon="pi pi-trash"
            [text]="true"
            size="small"
            severity="danger"
            [loading]="lifecycleSaving()"
            [ariaLabel]="'DOCUMENT_MANAGEMENT.DELETE_PERMANENTLY' | translate"
            [pTooltip]="'DOCUMENT_MANAGEMENT.DELETE_PERMANENTLY' | translate"
            (onClick)="onHardDelete()" />
        } @else if (canSoftDelete()) {
          <p-button
            icon="pi pi-trash"
            [text]="true"
            size="small"
            severity="danger"
            [loading]="lifecycleSaving()"
            [ariaLabel]="'DOCUMENT_MANAGEMENT.DELETE' | translate"
            [pTooltip]="'DOCUMENT_MANAGEMENT.DELETE' | translate"
            (onClick)="onSoftDelete()" />
        }
      </div>
    </header>

    @if (lifecycleError() || starError()) {
      <p class="detail-pane__lifecycle-error" role="alert">{{ lifecycleError() ?? starError() }}</p>
    }

    @if (canOpenInOverlay() && document()!.currentVersionId) {
      <button
        type="button"
        class="detail-pane__preview-tile"
        [class.detail-pane__preview-tile--placeholder]="!previewUrl()"
        [attr.aria-label]="'DOCUMENT_MANAGEMENT.OPEN_PREVIEW' | translate"
        (click)="onOpenPreview()">
        @if (previewUrl(); as url) {
          <img [src]="url" alt="" class="detail-pane__preview-image" />
        } @else if (previewLoading()) {
          <p-progressSpinner class="detail-pane__preview-spinner" strokeWidth="3" />
        } @else {
          <span class="detail-pane__preview-placeholder">
            <i class="pi pi-eye" aria-hidden="true"></i>
            <span>{{ 'DOCUMENT_MANAGEMENT.OPEN_PREVIEW' | translate }}</span>
          </span>
        }
        <span class="detail-pane__preview-hover">
          {{ 'DOCUMENT_MANAGEMENT.OPEN_PREVIEW' | translate }}
        </span>
      </button>
    }

    <div class="detail-pane__actions">
      <p-button
        [label]="'DOCUMENT_MANAGEMENT.DOWNLOAD' | translate"
        icon="pi pi-download"
        size="small"
        [outlined]="true"
        [loading]="downloadInFlight()"
        [disabled]="!document()!.currentVersionId"
        (onClick)="onDownload()" />
      @if (canUploadNewVersion()) {
        <p-button
          [label]="'DOCUMENT_MANAGEMENT.UPLOAD_NEW_VERSION' | translate"
          icon="pi pi-upload"
          size="small"
          [outlined]="true"
          [loading]="isUploadingNewVersion()"
          (onClick)="onUploadNewVersionClick()" />
      }
    </div>

    <input
      #newVersionInput
      type="file"
      class="detail-pane__file-input"
      aria-hidden="true"
      tabindex="-1"
      (change)="onNewVersionFileChange($event)" />

    @if (newVersionError()) {
      <div class="detail-pane__new-version-error" role="alert">
        <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
        <span>{{ newVersionError() }}</span>
        <p-button
          icon="pi pi-times"
          [text]="true"
          size="small"
          [ariaLabel]="'GENERAL.DISMISS' | translate"
          (onClick)="dismissNewVersionError()" />
      </div>
    }

    @if (canEditDescription() || document()!.description) {
      <section class="detail-pane__section">
        <div class="detail-pane__section-header">
          <h3 class="detail-pane__section-title">
            {{ 'DOCUMENT_MANAGEMENT.DESCRIPTION' | translate }}
          </h3>
          @if (!descriptionEditing() && canEditDescription()) {
            <p-button
              icon="pi pi-pencil"
              [text]="true"
              size="small"
              [ariaLabel]="
                (document()!.description
                  ? 'DOCUMENT_MANAGEMENT.EDIT_DESCRIPTION'
                  : 'DOCUMENT_MANAGEMENT.ADD_DESCRIPTION'
                ) | translate
              "
              [pTooltip]="
                (document()!.description
                  ? 'DOCUMENT_MANAGEMENT.EDIT_DESCRIPTION'
                  : 'DOCUMENT_MANAGEMENT.ADD_DESCRIPTION'
                ) | translate
              "
              (onClick)="onStartEditDescription()" />
          }
        </div>

        @if (descriptionEditing()) {
          <label class="detail-pane__description-label">
            <span class="detail-pane__visually-hidden">
              {{ 'DOCUMENT_MANAGEMENT.DESCRIPTION' | translate }}
            </span>
            <textarea
              class="detail-pane__description-textarea"
              rows="4"
              placeholder=" "
              [value]="descriptionDraft()"
              [disabled]="descriptionSaving()"
              [attr.placeholder]="'DOCUMENT_MANAGEMENT.DESCRIPTION_PLACEHOLDER' | translate"
              (input)="onDescriptionInput($event)"></textarea>
          </label>
          @if (descriptionError()) {
            <p class="detail-pane__description-error" role="alert">{{ descriptionError() }}</p>
          }
          <div class="detail-pane__description-actions">
            <p-button
              [label]="'GENERAL.CANCEL' | translate"
              size="small"
              [text]="true"
              [disabled]="descriptionSaving()"
              (onClick)="onCancelEditDescription()" />
            <p-button
              [label]="'GENERAL.SAVE' | translate"
              size="small"
              [loading]="descriptionSaving()"
              (onClick)="onSaveDescription()" />
          </div>
        } @else if (document()!.description) {
          @if (canEditDescription()) {
            <button
              type="button"
              class="detail-pane__description detail-pane__description--editable"
              [attr.aria-label]="'DOCUMENT_MANAGEMENT.EDIT_DESCRIPTION' | translate"
              (click)="onStartEditDescription()">
              {{ document()!.description }}
            </button>
          } @else {
            <p class="detail-pane__description">{{ document()!.description }}</p>
          }
        } @else if (canEditDescription()) {
          <button
            type="button"
            class="detail-pane__description-empty detail-pane__description-empty--editable"
            [attr.aria-label]="'DOCUMENT_MANAGEMENT.ADD_DESCRIPTION' | translate"
            (click)="onStartEditDescription()">
            {{ 'DOCUMENT_MANAGEMENT.NO_DESCRIPTION' | translate }}
          </button>
        }
      </section>
    }

    <section class="detail-pane__section">
      <h3 class="detail-pane__section-title">{{ 'TAGS.SECTION_TITLE' | translate }}</h3>
      <tf-tag-editor
        [tags]="document()!.tags"
        [availableTags]="availableTags()"
        [editable]="canEditTags()"
        [canCreate]="canCreateTags()"
        (tagsChange)="onTagsChange($event)"
        (tagCreate)="onTagCreate($event)"
        (searchOpened)="onTagSearchOpened()" />
    </section>

    <section class="detail-pane__section">
      <h3 class="detail-pane__section-title">{{ 'DOCUMENT_MANAGEMENT.DETAILS' | translate }}</h3>
      <dl class="detail-pane__kv">
        <div class="detail-pane__kv-row">
          <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_MIME' | translate }}</dt>
          <dd>{{ document()!.mimeType ?? '—' }}</dd>
        </div>
        <div class="detail-pane__kv-row">
          <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_SIZE' | translate }}</dt>
          <dd>{{ formatSize(document()!.size) }}</dd>
        </div>
        @if (isSoftDeleted() && originalPath().length > 0) {
          <div class="detail-pane__kv-row">
            <dt>{{ 'DOCUMENT_MANAGEMENT.ORIGINAL_PATH' | translate }}</dt>
            <dd class="detail-pane__deleted-path-segments">/{{ originalPath().join('/') }}/</dd>
          </div>
        }
        <div class="detail-pane__kv-row">
          <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_CREATED_AT' | translate }}</dt>
          <dd>
            @if (document()!.createdAt) {
              {{ document()!.createdAt | date: 'M/d/yyyy, h:mm a' }}
            } @else {
              —
            }
          </dd>
        </div>
        @if (document()!.createdByName || document()!.createdBy) {
          <div class="detail-pane__kv-row">
            <dt>{{ 'DOCUMENT_MANAGEMENT.DETAIL_CREATED_BY' | translate }}</dt>
            @if (document()!.createdByName) {
              <dd>{{ document()!.createdByName }}</dd>
            } @else {
              <dd class="detail-pane__id-mono">{{ document()!.createdBy }}</dd>
            }
          </div>
        }
      </dl>
    </section>

    @if (!isSoftDeleted() || extraction()) {
      <section class="detail-pane__section detail-pane__section--collapsible">
        <div class="detail-pane__section-header">
          <h3 class="detail-pane__section-title">
            <button
              type="button"
              class="detail-pane__section-toggle"
              [attr.aria-expanded]="isAiAnalysisExpanded()"
              [attr.aria-label]="
                (isAiAnalysisExpanded()
                  ? 'DOCUMENT_MANAGEMENT.COLLAPSE_AI_ANALYZE'
                  : 'DOCUMENT_MANAGEMENT.EXPAND_AI_ANALYZE'
                ) | translate
              "
              (click)="onToggleAiAnalysisExpanded()">
              <i
                class="pi"
                [class.pi-chevron-right]="!isAiAnalysisExpanded()"
                [class.pi-chevron-down]="isAiAnalysisExpanded()"
                aria-hidden="true"></i>
              <span>{{ 'DOCUMENT_MANAGEMENT.AI_ANALYZE' | translate }}</span>
            </button>
          </h3>
          @if (isAiAnalysisExpanded()) {
            @if (extraction(); as ex) {
              @if (ex.status === 'succeeded' && canAnalyze()) {
                <p-button
                  [label]="'DOCUMENT_MANAGEMENT.RE_ANALYZE' | translate"
                  icon="pi pi-refresh"
                  size="small"
                  [text]="true"
                  [loading]="extractionStarting() || isExtractionRunning()"
                  [disabled]="!document()!.currentVersionId"
                  (onClick)="onAnalyze()" />
              }
            }
          }
        </div>

        @if (isAiAnalysisExpanded()) {
          @if (extractionError()) {
            <div class="detail-pane__new-version-error" role="alert">
              <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
              <span>{{ extractionError() }}</span>
              <p-button
                icon="pi pi-times"
                [text]="true"
                size="small"
                [ariaLabel]="'GENERAL.DISMISS' | translate"
                (onClick)="onDismissExtractionError()" />
            </div>
          }

          @if (extraction(); as ex) {
            @if (isExtractionRunning() || extractionStarting()) {
              <div class="detail-pane__analyze-status" aria-live="polite">
                <span class="detail-pane__analyze-spark" aria-hidden="true">
                  <i class="pi pi-sparkles"></i>
                </span>
                <span>{{ 'DOCUMENT_MANAGEMENT.ANALYZING' | translate }}</span>
              </div>
            } @else if (ex.status === 'succeeded') {
              <p class="detail-pane__analyze-meta">
                @if (ex.finishedAt) {
                  {{ 'DOCUMENT_MANAGEMENT.ANALYZED_AT' | translate }}
                  {{ ex.finishedAt | date: 'M/d/yyyy, h:mm a' }}
                }
              </p>
              @if (extractedFieldRows().length > 0) {
                <dl class="detail-pane__kv">
                  @for (row of extractedFieldRows(); track row.key) {
                    <div class="detail-pane__kv-row">
                      <dt>{{ row.key }}</dt>
                      <dd>{{ row.value }}</dd>
                    </div>
                  }
                </dl>
              } @else {
                <p class="detail-pane__analyze-empty">
                  {{ 'DOCUMENT_MANAGEMENT.ANALYSIS_NO_FIELDS' | translate }}
                </p>
              }
            } @else if (ex.status === 'failed' || ex.status === 'cancelled') {
              <p class="detail-pane__analyze-failed">
                {{ 'DOCUMENT_MANAGEMENT.ANALYSIS_FAILED_BANNER' | translate }}
              </p>
              @if (canAnalyze()) {
                <p-button
                  [label]="'DOCUMENT_MANAGEMENT.RETRY' | translate"
                  icon="pi pi-refresh"
                  size="small"
                  [outlined]="true"
                  [disabled]="!document()!.currentVersionId"
                  (onClick)="onAnalyze()" />
              }
            }
          } @else {
            <p class="detail-pane__analyze-empty">
              {{ 'DOCUMENT_MANAGEMENT.NO_ANALYSIS_YET' | translate }}
            </p>
            @if (canAnalyze()) {
              <p-button
                class="detail-pane__analyze-action"
                [label]="'DOCUMENT_MANAGEMENT.ANALYZE' | translate"
                icon="pi pi-sparkles"
                size="small"
                [outlined]="true"
                [loading]="extractionStarting()"
                [disabled]="!document()!.currentVersionId"
                (onClick)="onAnalyze()" />
            }
          }

          @if (extraction()) {
            <button type="button" class="detail-pane__history-toggle" (click)="onToggleHistory()">
              <i
                class="pi"
                [class.pi-chevron-right]="!historyExpanded()"
                [class.pi-chevron-down]="historyExpanded()"
                aria-hidden="true"></i>
              {{ 'DOCUMENT_MANAGEMENT.ANALYSIS_HISTORY' | translate }}
            </button>
            @if (historyExpanded() && extractionHistory().length > 0) {
              <ul class="detail-pane__history">
                @for (entry of extractionHistory(); track entry.id) {
                  <li>
                    <button
                      type="button"
                      class="detail-pane__history-row"
                      [class.detail-pane__history-row--selected]="isExtractionSelected(entry)"
                      (click)="onSelectHistoryEntry(entry)">
                      <span
                        class="detail-pane__history-status"
                        [class.detail-pane__history-status--succeeded]="
                          entry.status === 'succeeded'
                        "
                        [class.detail-pane__history-status--failed]="
                          entry.status === 'failed' || entry.status === 'cancelled'
                        "
                        [class.detail-pane__history-status--running]="
                          entry.status === 'pending' || entry.status === 'running'
                        ">
                        {{
                          'DOCUMENT_MANAGEMENT.EXTRACTION_STATUS.' + (entry.status ?? 'pending')
                            | translate
                        }}
                      </span>
                      <span class="detail-pane__history-date">
                        @if (entry.finishedAt ?? entry.createdAt; as t) {
                          {{ t | date: 'M/d/yyyy, h:mm a' }}
                        }
                      </span>
                    </button>
                  </li>
                }
              </ul>
            }
          }
        }
      </section>
    }

    @if (versions().length > 0) {
      <section class="detail-pane__section">
        <h3 class="detail-pane__section-title">
          {{ 'DOCUMENT_MANAGEMENT.VERSIONS' | translate }} ({{ versions().length }})
        </h3>
        <ul class="detail-pane__versions">
          @for (version of versions(); track version.id) {
            <li
              class="detail-pane__version"
              [class.detail-pane__version--current]="isCurrentVersion(version)"
              [class.detail-pane__version--pending]="isPendingVersion(version)">
              <span class="detail-pane__version-tag">v{{ version.versionNumber }}</span>
              <span class="detail-pane__version-meta">
                <span
                  class="detail-pane__version-line"
                  [title]="version.originalFileName ?? document()!.name">
                  {{ version.originalFileName ?? document()!.name }}
                </span>
                <span class="detail-pane__version-sub">
                  @if (version.uploadedByName) {
                    {{ version.uploadedByName }}
                  }
                  @if (version.uploadedByName && version.uploadedAt) {
                    ·
                  }
                  @if (version.uploadedAt) {
                    <span
                      [pTooltip]="(version.uploadedAt | date: 'medium') ?? ''"
                      tooltipPosition="top"
                      [appendTo]="'body'">
                      {{ version.uploadedAt | date: 'M/d/yyyy, h:mm a' }}
                    </span>
                  }
                  @if (isCurrentVersion(version)) {
                    · {{ 'DOCUMENT_MANAGEMENT.VERSION_CURRENT' | translate }}
                  }
                  @if (isPendingVersion(version)) {
                    · {{ 'DOCUMENT_MANAGEMENT.UPLOAD_INCOMPLETE' | translate }}
                  }
                </span>
                @if (version.changeNote) {
                  <span class="detail-pane__version-note">{{ version.changeNote }}</span>
                }
              </span>
              @if (!isPendingVersion(version)) {
                <p-button
                  icon="pi pi-download"
                  [text]="true"
                  size="small"
                  [loading]="versionDownloadingId() === version.id"
                  [ariaLabel]="'DOCUMENT_MANAGEMENT.DOWNLOAD' | translate"
                  [pTooltip]="'DOCUMENT_MANAGEMENT.DOWNLOAD' | translate"
                  (onClick)="onDownloadVersion(version)" />
              }
            </li>
          }
        </ul>
      </section>
    }
  }

  <p-confirmDialog styleClass="detail-pane__confirm-dialog" />

  <p-dialog
    [visible]="restoreRenameDialogVisible()"
    (visibleChange)="$event ? null : onRestoreRenameCancel()"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="!lifecycleSaving()"
    [header]="'DOCUMENT_MANAGEMENT.RESTORE_RENAME_TITLE' | translate"
    styleClass="detail-pane__restore-rename-dialog detail-pane__compact-dialog">
    <div class="detail-pane__restore-rename-body">
      <p class="detail-pane__restore-rename-message">
        {{ 'DOCUMENT_MANAGEMENT.RESTORE_RENAME_MESSAGE' | translate }}
      </p>
      <label class="detail-pane__restore-rename-label" for="detail-pane-restore-rename-input">
        {{ 'DOCUMENT_MANAGEMENT.RESTORE_RENAME_LABEL' | translate }}
      </label>
      <input
        pInputText
        id="detail-pane-restore-rename-input"
        class="detail-pane__restore-rename-input"
        [value]="restoreRenameDraft()"
        [disabled]="lifecycleSaving()"
        [attr.aria-invalid]="restoreRenameError() ? true : null"
        [attr.aria-describedby]="restoreRenameError() ? 'detail-pane-restore-rename-error' : null"
        (input)="onRestoreRenameInput($event)"
        (keydown.enter)="onRestoreRenameSave()" />
      @if (restoreRenameError()) {
        <small
          id="detail-pane-restore-rename-error"
          class="detail-pane__restore-rename-error"
          role="alert">
          {{ restoreRenameError() }}
        </small>
      }
    </div>
    <ng-template pTemplate="footer">
      <p-button
        [label]="'GENERAL.CANCEL' | translate"
        severity="secondary"
        [outlined]="true"
        [disabled]="lifecycleSaving()"
        (onClick)="onRestoreRenameCancel()" />
      <p-button
        [label]="'DOCUMENT_MANAGEMENT.RESTORE' | translate"
        severity="primary"
        [loading]="lifecycleSaving()"
        (onClick)="onRestoreRenameSave()" />
    </ng-template>
  </p-dialog>
</aside>
